import { Container, Typography } from '@material-ui/core'
import { NextSeo } from 'next-seo'
import React from 'react'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { makeStyles } from '@material-ui/core/styles'
import { Chip, Grid } from '@material-ui/core'

import { urls } from '../src/utils/config'
import Nav from '../src/components/Nav'
import Footer from '../src/components/Footer'
import JoinCommunitySection from '../src/components/Footer/JoinCommunitySection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Image from 'next/image'

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 0,
    '& p, & h6, & h4': {
      color: 'white',
    },
  },
  pageWrapper: {
    background: 'black',
    overflow: 'auto',
  },
  pageGradient: {
    background: 'linear-gradient(180deg, rgba(137, 93, 209, 0.3) , black 50% )',
    overflow: 'hidden',
  },
  nav: {
    marginTop: 0,
  },
  root: {
    margin: 'auto',
    maxWidth: '1200px',
  },
  contentSection: {
    margin: '40px 0',
    padding: '20px',
    [theme.breakpoints.down('xs')]: {
      margin: '40px 0',
    },
    [theme.breakpoints.up('sm')]: {
      margin: '50px 0',
    },
    [theme.breakpoints.up('md')]: {
      margin: '120px 0',
    },
  },
  section: {},
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  divider: {
    margin: '40px 0',
    [theme.breakpoints.up('md')]: {
      margin: '80px 0',
    },
  },
  aboveFold: {
    minHeight: '20vh',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: -1,
  },
  title: {
    color: 'white',
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
      maxWidth: '100%',
      margin: '0 auto 0 auto',
    },
  },
  subtitle: {
    marginTop: '20px',
    marginBottom: '30px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  heroContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '400px',
    [theme.breakpoints.up('sm')]: {
      minHeight: '300px',
    },
  },
  heroTextSection: {
    width: '100%',
    padding: '20px',
    textAlign: 'center',
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
  chip: {
    padding: '4px',
    width: 'fit-content',
    marginBottom: '4px',
    background: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '1rem',
    color: 'white',
  },
  detailsContainer: {
    padding: '20px',
    marginTop: '8rem',
    '& h4, & p': {
      fontFamily: theme.typography.h1.fontFamily,
      margin: '2rem 0',
    },
  },
  whySection: {
    marginTop: '4rem',
    '& h4, & p': {
      fontFamily: theme.typography.h1.fontFamily,
      margin: '2rem 0',
    },
    '& > div': {
      marginTop: '4rem',
    },
  },
  darkText: {
    color: '#7D7787',
  },
  why: {
    margin: '4rem 0',
  },
}))

const About = (): JSX.Element => {
  const classes = useStyles({})

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.about,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - About us`}
            description={`About Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - About us`,
              description: `About Cryptotesters.com.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 145,
                  height: 56,
                  alt: `Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.pageGradient}>
              <Nav />
              <Container className={classes.container} maxWidth="lg">
                <div className={classes.heroContent}>
                  <div className={classes.heroTextSection}>
                    <Chip label="About Us" className={classes.chip} />
                    <Typography variant="h1" className={classes.title}>
                      The crypto space is a place of
                      <span className={classes.purpleText}> permissionless innovation</span>
                    </Typography>
                    <Typography variant="subtitle2" className={classes.subtitle}>
                      A dream for builders and hackers who release new products to the market every
                      week. This proliferation of products is a blessing for the space. Many
                      approaches and concepts need to be tested to ultimately find out what works.
                    </Typography>
                  </div>
                </div>
                <section className={classes.detailsContainer}>
                  <Grid container>
                    <Grid md={6} xs={12} item justify="center" container>
                      <Image src={'/coins-1.png'} height={360} width={360} />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Typography variant="h4">
                        We want to become the one-stop address for people curious but new to the
                        crypto space.
                      </Typography>
                      <Typography variant="body2">
                        Historically, crypto companies have been bad at marketing their products to
                        mainstream audiences. We want to change that. We will look out for the
                        wallets with the best user experience to give people new to the crypto place
                        the best introduction.
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid item md={6} xs={12}>
                      <Typography variant="h4">
                        Crypto was designed to be a peer-to-peer system so we will not promote
                        custodial wallets that work like banks
                      </Typography>
                      <Typography variant="body2">
                        We’ll feature wallets that do well at reducing and hiding a lot of
                        Blockchain related complexity. Wallets that make Blockchain feel and look
                        like traditional fintech apps. Wallets that communicate complex issues in an
                        understandable way to the user. Wallets that let users access a variety of
                        Blockchain features, not simply sending & receiving.
                      </Typography>
                    </Grid>
                    <Grid md={6} xs={12} item justify="center" container>
                      <Image src={'/coins-2.png'} height={360} width={360} />
                    </Grid>
                  </Grid>

                  <Grid container>
                    <Grid md={6} xs={12} item justify="center" container>
                      <Image src={'/coins-3.png'} height={360} width={360} />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Typography variant="h4">
                        Our ranking will only be guided by these principles.
                      </Typography>
                      <Typography variant="body2">
                        To give a few examples in the current landscape: wallets that have keyless
                        recovery, fiat on-ramps and natively integrated DeFi protocols would rank
                        well.
                      </Typography>
                    </Grid>
                  </Grid>
                </section>

                <section className={classes.whySection}>
                  <Grid container justify="center">
                    <Grid item xs={10} md={5}>
                      <div className={classes.why}>
                        <Image src={'/svg/lifebelt.svg'} height={64} width={64} />
                        <Typography variant="body2">
                          At cryptotesters, we want to help people who are new to crypto by
                          recommending the best products to them.{' '}
                          <span className={classes.darkText}>
                            We further believe that exchanges are not the place to begin your
                            blockchain journey.
                          </span>
                        </Typography>
                      </div>

                      <div className={classes.why}>
                        <Image src={'/svg/refresh-double.svg'} height={64} width={64} />
                        <Typography variant="body2">
                          Why?{' '}
                          <span className={classes.darkText}>
                            Exchanges unsafe, confusing and the process of withdrawing crypto to a
                            separate wallet is too cumbersome to scale to a billion people.
                          </span>
                        </Typography>
                      </div>

                      <div className={classes.why}>
                        <Image src={'/svg/wallet.svg'} height={64} width={64} />
                        <Typography variant="body2">
                          We believe that a user’s blockchain journey should begin by downloading a
                          mobile wallet,{' '}
                          <span className={classes.darkText}>
                            buying crypto within the wallet and begin interacting with the
                            underlying Blockchain protocols right away.
                          </span>
                        </Typography>
                      </div>

                      <div className={classes.why}>
                        <Image src={'/svg/lifebelt.svg'} height={64} width={64} />
                        <Typography variant="body2">
                          The wallet ecosystem is growing and improving everyday and users should
                          find the best wallets to get the best possible experience.{' '}
                          <span className={classes.darkText}>
                            That’s why we are launching a wallet review platform, that compares and
                            ranks existing wallets.
                          </span>
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </section>
              </Container>
              <div style={{ background: 'white' }}>
                <QuickLinks />
                <JoinCommunitySection />
                <Footer />
              </div>
            </div>
          </div>
        </>
      )}
    </Amplitude>
  )
}

export default About
